import React, { useState, useMemo, useCallback } from "react";
import { Calendar, Clock, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS_FR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const TIME_SLOTS = [
  { label: "09:00", value: "09:00" },
  { label: "09:30", value: "09:30" },
  { label: "10:00", value: "10:00" },
  { label: "10:30", value: "10:30" },
  { label: "11:00", value: "11:00" },
  { label: "11:30", value: "11:30" },
  { label: "14:00", value: "14:00" },
  { label: "14:30", value: "14:30" },
  { label: "15:00", value: "15:00" },
  { label: "15:30", value: "15:30" },
  { label: "16:00", value: "16:00" },
  { label: "16:30", value: "16:30" },
];

/**
 * Composant de prise de RDV natif intégré dans les résultats du diagnostic.
 * Affiche un calendrier de 14 jours et des créneaux horaires.
 */
export default function DiagnosticRDV({ contact, diagnosticId }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isBooked, setIsBooked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate next 14 weekdays
  const availableDays = useMemo(() => {
    const days = [];
    const now = new Date();
    let d = new Date(now);
    d.setDate(d.getDate() + 1); // Start tomorrow
    while (days.length < 14) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) { // Skip weekends
        days.push(new Date(d));
      }
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, []);

  const handleBook = useCallback(async () => {
    if (!selectedDate || !selectedTime) return;
    setIsSubmitting(true);

    const dateStr = selectedDate.toISOString().split("T")[0];
    const rdvLabel = `${DAYS_FR[selectedDate.getDay()]} ${selectedDate.getDate()} ${MONTHS_FR[selectedDate.getMonth()]} à ${selectedTime}`;

    try {
      // Store booking
      await axios.post(`${API}/api/diagnostic/rdv`, {
        diagnostic_id: diagnosticId,
        contact,
        date: dateStr,
        time: selectedTime,
        rdv_label: rdvLabel,
      });

      setIsBooked(true);
      toast.success(`RDV confirmé : ${rdvLabel}`);

      if (typeof window.fbq === "function") window.fbq("track", "Schedule", { content_name: "diagnostic_rdv" });
      if (typeof window.gtag === "function") window.gtag("event", "Schedule", { content_name: "diagnostic_rdv" });
    } catch {
      toast.error("Erreur lors de la prise de RDV. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedDate, selectedTime, contact, diagnosticId]);

  if (isBooked) {
    const rdvLabel = `${DAYS_FR[selectedDate.getDay()]} ${selectedDate.getDate()} ${MONTHS_FR[selectedDate.getMonth()]} à ${selectedTime}`;
    return (
      <div className="bg-green-50 border-2 border-green-200 rounded-md p-6 text-center" data-testid="rdv-confirmed">
        <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-green-900 mb-1">RDV confirmé !</h3>
        <p className="text-sm text-green-700 mb-2">{rdvLabel}</p>
        <p className="text-xs text-green-600">
          Un expert Lead Artisan vous appellera à ce créneau pour discuter de votre diagnostic.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-slate-200 rounded-md p-6" data-testid="rdv-picker" style={{ boxShadow: "4px 4px 0px rgba(30,90,158,0.1)" }}>
      <div className="flex items-center gap-2 mb-5">
        <Calendar className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-bold text-slate-900">Réservez votre appel stratégie</h3>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        30 minutes avec un expert Lead Artisan pour analyser votre diagnostic et définir un plan d'action.
      </p>

      {/* Date picker - scrollable row */}
      <div className="mb-5">
        <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">Choisissez un jour</p>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
          {availableDays.map((day) => {
            const isSelected = selectedDate?.toDateString() === day.toDateString();
            return (
              <button
                key={day.toISOString()}
                onClick={() => { setSelectedDate(day); setSelectedTime(null); }}
                data-testid={`rdv-date-${day.getDate()}`}
                className={`flex-shrink-0 w-16 py-3 rounded-md text-center border-2 transition-all ${
                  isSelected
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-slate-200 hover:border-slate-300 text-slate-600"
                }`}
              >
                <p className="text-[0.6rem] font-semibold uppercase">{DAYS_FR[day.getDay()]}</p>
                <p className="text-lg font-black">{day.getDate()}</p>
                <p className="text-[0.6rem]">{MONTHS_FR[day.getMonth()].slice(0, 3)}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Time slots */}
      {selectedDate && (
        <div className="mb-5">
          <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Choisissez un créneau
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {TIME_SLOTS.map((slot) => {
              const isSelected = selectedTime === slot.value;
              return (
                <button
                  key={slot.value}
                  onClick={() => setSelectedTime(slot.value)}
                  data-testid={`rdv-time-${slot.value}`}
                  className={`py-2.5 rounded-md text-sm font-medium border-2 transition-all ${
                    isSelected
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-slate-200 hover:border-slate-300 text-slate-600"
                  }`}
                >
                  {slot.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirm button */}
      {selectedDate && selectedTime && (
        <Button
          onClick={handleBook}
          disabled={isSubmitting}
          data-testid="rdv-confirm"
          className="w-full bg-accent hover:bg-orange-600 text-white font-bold py-4 rounded-md transition-all hover:scale-[1.02]"
        >
          {isSubmitting ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <Calendar className="w-5 h-5 mr-2" />
          )}
          Confirmer — {DAYS_FR[selectedDate.getDay()]} {selectedDate.getDate()} {MONTHS_FR[selectedDate.getMonth()].slice(0, 3)} à {selectedTime}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      )}
    </div>
  );
}
